import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiRefreshCw, FiCheckCircle, FiXCircle, FiPrinter } from 'react-icons/fi';

const ResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { results, questions, answers, config } = location.state || {};

  if (!results) {
    navigate('/dashboard');
    return null;
  }

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: '#10b981', message: 'Outstanding!' };
    if (percentage >= 80) return { grade: 'A', color: '#22c55e', message: 'Excellent!' };
    if (percentage >= 70) return { grade: 'B', color: '#3b82f6', message: 'Good Job!' };
    if (percentage >= 60) return { grade: 'C', color: '#f59e0b', message: 'Nice Try!' };
    if (percentage >= 50) return { grade: 'D', color: '#f97316', message: 'Keep Practicing!' };
    return { grade: 'F', color: '#ef4444', message: 'Need Improvement!' };
  };

  const { grade, color, message } = getGrade(results.percentage);
  const wrongAnswers = results.total - results.score;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Result Summary Card */}
        <div className="card text-center mb-8">
          <div className="mb-6">
            <div 
              className="w-32 h-32 rounded-full border-8 flex flex-col items-center justify-center mx-auto mb-4 shadow-lg"
              style={{ borderColor: color }}
            >
              <div className="text-5xl font-bold" style={{ color }}>
                {grade}
              </div>
              <div className="text-sm text-gray-600 mt-1">{results.percentage}%</div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              {message}
            </h1>
            <p className="text-gray-600 text-lg">
              You scored {results.score} out of {results.total}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-600 text-sm mb-1">Total Questions</p>
              <p className="text-3xl font-bold text-gray-800">{results.total}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-6">
              <p className="text-green-600 text-sm mb-1">Correct</p>
              <p className="text-3xl font-bold text-green-600">{results.score}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-6">
              <p className="text-red-600 text-sm mb-1">Wrong</p>
              <p className="text-3xl font-bold text-red-600">{wrongAnswers}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-secondary flex-1 flex items-center justify-center"
            >
              <FiHome className="mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary flex-1 flex items-center justify-center"
            >
              <FiRefreshCw className="mr-2" />
              Take Another Quiz
            </button>
            <button
              onClick={() => window.print()}
              className="btn-secondary flex items-center justify-center sm:flex-initial px-8"
            >
              <FiPrinter className="mr-2" />
              Print
            </button>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Detailed Review</h2>
          <div className="space-y-4">
            {results.results && results.results.map((result, index) => {
              const question = questions?.find(q => q.id === result.question_id) || {};
              const options = config?.useAI 
                ? question.options || []
                : [
                    { label: 'A', text: question.opt_a },
                    { label: 'B', text: question.opt_b },
                    { label: 'C', text: question.opt_c },
                    { label: 'D', text: question.opt_d },
                  ].filter(opt => opt.text);

              return (
                <div
                  key={index}
                  className={`p-6 rounded-lg border-2 ${
                    result.is_correct
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  {/* Question Header */}
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-semibold text-gray-800 text-lg flex-1">
                      Question {index + 1}
                    </h3>
                    {result.is_correct ? (
                      <div className="flex items-center text-green-600 font-semibold">
                        <FiCheckCircle className="mr-2" />
                        Correct
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600 font-semibold">
                        <FiXCircle className="mr-2" />
                        Wrong
                      </div>
                    )}
                  </div>

                  {/* Question Text */}
                  <p className="text-gray-800 mb-4 text-lg">
                    {result.question || question.question}
                  </p>

                  {/* Options */}
                  <div className="space-y-2 mb-4">
                    {options.map((option) => {
                      const optionLabel = config?.useAI 
                        ? String.fromCharCode(65 + options.indexOf(option))
                        : option.label;
                      const optionText = config?.useAI ? option.text : option.text;
                      const isCorrect = config?.useAI 
                        ? option.isCorrect 
                        : optionLabel === question.correct;
                      const isSelected = result.selected === optionLabel;

                      return (
                        <div
                          key={optionLabel}
                          className={`p-3 rounded-lg border-2 ${
                            isCorrect
                              ? 'border-green-500 bg-green-100'
                              : isSelected
                              ? 'border-red-500 bg-red-100'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                              isCorrect
                                ? 'bg-green-500 text-white'
                                : isSelected
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {optionLabel}
                            </div>
                            <span className="flex-1">{optionText}</span>
                            {isCorrect && <FiCheckCircle className="text-green-600" />}
                            {isSelected && !isCorrect && <FiXCircle className="text-red-600" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Answer Info */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-semibold">Your Answer:</span>{' '}
                        <span className={result.is_correct ? 'text-green-600' : 'text-red-600'}>
                          {result.selected}
                        </span>
                      </p>
                      {!result.is_correct && (
                        <p>
                          <span className="font-semibold">Correct Answer:</span>{' '}
                          <span className="text-green-600">{result.correct}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Explanation */}
                  {result.explanation && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-semibold text-blue-800 mb-2">💡 Explanation:</p>
                      <p className="text-gray-700">{result.explanation}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 print:hidden">
          <button
            onClick={() => navigate('/history')}
            className="btn-secondary flex-1"
          >
            View History
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary flex-1"
          >
            Take Another Quiz
          </button>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ResultPage;

